import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Mail, Sparkles, User } from 'lucide-react';
import { api } from '../api/client';
import { useAuthStore } from '../store/auth';
import AuthLayout from '../components/AuthLayout';
import ErrorBanner from '../components/ErrorBanner';

export default function Register() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/register', form);
      login(data.token, data.user);
      navigate('/domains/new');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Start tracking AI-driven traffic in minutes."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-brand-600 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">Name (optional)</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              value={form.name}
              onChange={onChange('name')}
              placeholder="Jane Doe"
            />
          </div>
        </div>
        <div>
          <label className="label">Work email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              type="email"
              required
              value={form.email}
              onChange={onChange('email')}
              autoComplete="email"
              placeholder="you@company.com"
            />
          </div>
        </div>
        <div>
          <label className="label">Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={onChange('password')}
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          </div>
        </div>
        {error && <ErrorBanner body={error} onDismiss={() => setError('')} />}
        <button className="btn-primary w-full" disabled={loading}>
          {loading ? (
            'Creating account…'
          ) : (
            <>
              <Sparkles className="w-4 h-4" /> Create account
            </>
          )}
        </button>
        <p className="text-xs text-ink-400 text-center">
          By signing up you agree to track AI traffic responsibly. No card required.
        </p>
      </form>
    </AuthLayout>
  );
}
